import { useEffect } from 'react';

/**
 * useSEO — set document title + meta tags secara dinamis
 * @param {object} opts
 * @param {string} opts.title       — judul halaman (tanpa suffix siteName)
 * @param {string} opts.description — meta description
 * @param {string} opts.image       — URL gambar untuk og:image
 * @param {string} opts.url         — canonical URL (opsional)
 * @param {string} opts.type        — og:type, default 'website'
 * @param {string} opts.siteName    — nama situs (dari SiteSettingsContext)
 */
export default function useSEO({ title, description, image, url, type = 'website', siteName = 'Lentera', noindex = false, seo = {} }) {
  useEffect(() => {
    // Judul memakai template dari SEO settings (%s = judul halaman, %site% = nama situs)
    const template = seo.titleTemplate || '%s — %site%';
    const baseTitle = seo.metaTitle || siteName;
    const fullTitle = title
      ? template.replace('%s', title).replace('%site%', siteName)
      : baseTitle;
    const desc = description || seo.metaDescription || '';
    const ogImage = image || seo.defaultOgImage || '';
    const robots = (noindex || seo.robotsIndex === false)
      ? 'noindex, nofollow'
      : `${seo.robotsIndex === false ? 'noindex' : 'index'}, ${seo.robotsFollow === false ? 'nofollow' : 'follow'}`;

    document.title = fullTitle;

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.match(/\[(.+?)="(.+?)"\]/)?.slice(1) || [];
        if (attrName) el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Standard meta
    setMeta('meta[name="description"]',          'content', desc);
    setMeta('meta[name="robots"]',               'content', robots);
    if (seo.keywords)           setMeta('meta[name="keywords"]', 'content', seo.keywords);
    if (seo.googleVerification) setMeta('meta[name="google-site-verification"]', 'content', seo.googleVerification);
    if (seo.bingVerification)   setMeta('meta[name="msvalidate.01"]', 'content', seo.bingVerification);
    if (seo.themeColor)         setMeta('meta[name="theme-color"]', 'content', seo.themeColor);

    // Open Graph
    setMeta('meta[property="og:title"]',         'content', fullTitle);
    setMeta('meta[property="og:description"]',   'content', desc);
    setMeta('meta[property="og:type"]',          'content', seo.ogType || type);
    setMeta('meta[property="og:site_name"]',     'content', siteName);
    setMeta('meta[property="og:locale"]',        'content', seo.locale || 'id_ID');
    if (ogImage) setMeta('meta[property="og:image"]', 'content', ogImage);
    if (url)     setMeta('meta[property="og:url"]',   'content', url);
    if (seo.facebookAppId) setMeta('meta[property="fb:app_id"]', 'content', seo.facebookAppId);

    // Twitter Card
    setMeta('meta[name="twitter:card"]',         'content', seo.twitterCard || (ogImage ? 'summary_large_image' : 'summary'));
    setMeta('meta[name="twitter:title"]',        'content', fullTitle);
    setMeta('meta[name="twitter:description"]',  'content', desc);
    if (ogImage) setMeta('meta[name="twitter:image"]', 'content', ogImage);
    if (seo.twitterSite)    setMeta('meta[name="twitter:site"]', 'content', seo.twitterSite);
    if (seo.twitterCreator) setMeta('meta[name="twitter:creator"]', 'content', seo.twitterCreator);

    // Canonical
    const canonicalUrl = url || (seo.canonicalBase ? seo.canonicalBase.replace(/\/$/, '') + window.location.pathname : '');
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    }
  }, [title, description, image, url, type, siteName, noindex, seo]);
}
